/* eslint-disable unicorn/consistent-function-scoping */
import Axios, { AxiosResponse, CancelTokenSource } from 'axios';
import moment from 'moment';
import { commandsActionNames } from './types';
import {
  GetAllCommandsByDeviceKeyRequest,
  GetTypeConfigurationRequest,
  PatchCommandsByDeviceKeyRequest,
  PostCommandsByDeviceKeyRequest,
} from '../../services/types';
import { getSoftwareStatusByDeviceKey } from '../configure/actions';
import { AppState, AppThunkDispatch } from '..';
import Command from '../../services/commands';
import { Command as CommandEntity } from '../../entities/commands-v1/command';
import { enqueueSnackbarAction } from '../snackbar/actions';
import FileTypeConfiguration from '../../services/file-type-configurations';
import { poll } from '../../globals/utils';
import { CommandResponse } from '../../entities/commands-v1/models';

let ajaxRequest: CancelTokenSource | null = null;

export const getCommandsByDeviceKey =
  (
    input: GetAllCommandsByDeviceKeyRequest,
    clearCurrentCommands?: boolean,
    outGoingTransactionId?: string,
  ) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    if (ajaxRequest) {
      ajaxRequest.cancel();
    }
    ajaxRequest = Axios.CancelToken.source();
    const {
      login: { client, envVariables },
    } = getState();

    const commandsCall = new Command(client, envVariables.commandsV1BaseUrl);
    try {
      if (clearCurrentCommands) {
        dispatch({ type: commandsActionNames.CLEAR_COMMANDS });
      }
      const response = await commandsCall.getAllCommandsByDeviceKey(input, ajaxRequest.token);
      dispatch({
        type: commandsActionNames.SET_COMMANDS,
        payload: response.data.commands,
      });
      if (response.data.responseMetadata?.nextCursor) {
        dispatch({
          type: commandsActionNames.SET_NEXT_CURSOR,
          payload: response.data.responseMetadata.nextCursor,
        });
      }
      if (
        outGoingTransactionId &&
        response.data?.commands?.every((command) => command.transactionId, outGoingTransactionId)
      )
        return true;
    } catch (error: any) {
        dispatch({ type: commandsActionNames.SET_COMMANDS, payload: [] });
    }
    return false;
  };

export const getCommandsFromMultipleDevices =
  (
    inputs: GetAllCommandsByDeviceKeyRequest[],
    clearCurrentCommands?: boolean,
  ) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      if (ajaxRequest) {
        ajaxRequest.cancel();
      }
      ajaxRequest = Axios.CancelToken.source();
      const {
        login: { client, envVariables },
      } = getState();

      const promises: Promise<AxiosResponse<CommandResponse>>[] = [];
      const commandsCall = new Command(client, envVariables.commandsV1BaseUrl);
      try {
        if (clearCurrentCommands) {
          dispatch({ type: commandsActionNames.CLEAR_COMMANDS });
        }
        inputs.forEach(input => promises.push(commandsCall.getAllCommandsByDeviceKey(input, ajaxRequest?.token)));
        const responses = await Promise.allSettled(promises);

        let mixedCommands: Command[] = [];
        responses
          .filter(
            (
              response: PromiseSettledResult<
                AxiosResponse<CommandResponse>
              >,
            ) => response.status === 'fulfilled',
          )
          .forEach((responseSuccessful: any) => { mixedCommands = mixedCommands.concat(responseSuccessful.value.data.commands); }
          );
        
        if(mixedCommands.length === 0){
          dispatch(
            enqueueSnackbarAction({
              message: 'No commands found!',
              options: { key: (Date.now() + Math.random()).toString(), variant: 'warning' },
            }),
          );
        }else{
          dispatch({
            type: commandsActionNames.SET_COMMANDS,
            payload: mixedCommands,
          });
          return true;
        }
      } catch (error: any) {
        if (!error.message.includes('404'))
          dispatch({ type: commandsActionNames.SET_COMMANDS, payload: [] });
      }
      return false;
    };

export const pollCommandsByDeviceKey =
  (input: GetAllCommandsByDeviceKeyRequest) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    if (ajaxRequest) {
      ajaxRequest.cancel();
    }
    ajaxRequest = Axios.CancelToken.source();
    const {
      login: { client, envVariables },
      commands: { commands },
    } = getState();
    const commandsApi = new Command(client, envVariables.commandsV1BaseUrl);
    try {
      const response = await commandsApi.getAllCommandsByDeviceKey(input, ajaxRequest.token);
      let commandsToSet = response.data.commands;
      if (input.cursor && commandsToSet) commandsToSet = [...commands, ...commandsToSet];
      const polledCommands = response.data.commands;
      if (polledCommands?.length !== commands.length) {
        dispatch({ type: commandsActionNames.SET_COMMANDS, payload: commandsToSet });
        if (response.data.responseMetadata?.nextCursor) {
          dispatch({
            type: commandsActionNames.SET_NEXT_CURSOR,
            payload: response.data.responseMetadata.nextCursor,
          });
        }
      }
    } catch (error: any) {
      const errorMessage = `${error.message}`;
      if (errorMessage.includes('404')) {
        dispatch({ type: commandsActionNames.CLEAR_COMMANDS });
      } else {
        const uniqueKey = moment().seconds(0).milliseconds(0).toISOString();
        dispatch(
          enqueueSnackbarAction({
            message: 'Command queue updating slower than expected',
            options: { key: uniqueKey, variant: 'warning' },
          }),
        );
      }
    }
  };

export const pollingWaitForNewCommandByDeviceKey =
  () => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      search: { selectedEntity },
      commands: { commands },
      login: { client, envVariables },
    } = getState();
    const deviceKey = selectedEntity?.deviceKey;
    if (deviceKey) {
      try {
        const getCommands = new Command(client, envVariables.commandsV1BaseUrl);
        const validate = (result: any) =>
          result.data.commands.length > commands.length ||
          result.data.commands[0].createdDateTime !== commands[0].createdDateTime;
        const pollingArguments = {
          callBack: async () => getCommands.getAllCommandsByDeviceKey({ deviceKey, statuses: '1' }),
          validate,
          interval: 5000,
          maxAttempts: 10,
        };
        const response: any = await poll(pollingArguments);
        dispatch({ type: commandsActionNames.SET_COMMANDS, payload: response.data.commands });
      } catch (error) {
        dispatch({ type: commandsActionNames.SET_COMMANDS, payload: [] });
      }
    }
  };

export const getFileTypeConfigs =
  () => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const input: GetTypeConfigurationRequest = {
      limit: 1000,
    };
    const fileTypeConfiguration = new FileTypeConfiguration(
      client,
      envVariables.fileTypeConfigV2BaseUrl,
    );
    const response = await fileTypeConfiguration.getFileTypeConfigurationRecords(input);
    const { fileTypeConfigs } = response.data;
    if (fileTypeConfigs) {
      dispatch({
        type: commandsActionNames.SET_FILE_TYPE_CONFIGS,
        payload: fileTypeConfigs,
      });
    }
  };

export const getCommandBodyByDeviceKey =
  (input: GetAllCommandsByDeviceKeyRequest) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const commands = new Command(client, envVariables.commandsV1BaseUrl);
    try {
      const response = await commands.getAllCommandsByDeviceKey(input);
      dispatch({ type: commandsActionNames.SET_COMMANDS, payload: response.data.commands });
    } catch (error) {
      dispatch({ type: commandsActionNames.SET_COMMANDS, payload: [] });
    }
  };

export const postCommandsByDeviceKeyAction =
  (input: PostCommandsByDeviceKeyRequest) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const { deviceKey } = input;
    const commands = new Command(client, envVariables.commandsV1BaseUrl);
    try {
      const response = await commands.postCommandsByDeviceKey(input);
      if (response.data) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Command queued successfully',
            options: { key: (Date.now() + Math.random()).toString(), variant: 'success' },
          }),
        );
        dispatch(getCommandsByDeviceKey({ deviceKey, statuses: '1' }));
      }
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: `Command queuing failed: ${error.toString()}`,
          options: { key: (Date.now() + Math.random()).toString(), variant: 'error' },
        }),
      );
    }
  };

export const updateCommandsByDeviceKey =
  (input: PatchCommandsByDeviceKeyRequest) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const commands = new Command(client, envVariables.commandsV1BaseUrl);
    try {
      const response = await commands.patchCommandsByDeviceKey(input);
      if (response.status === 200) {
        dispatch(
          enqueueSnackbarAction({
            message:
              'Selected item(s) have been successfully removed from the Device Command Queue.',
            options: { key: (Date.now() + Math.random()).toString(), variant: 'success' },
          }),
        );
        dispatch(getCommandsByDeviceKey({ deviceKey: input.deviceKey, statuses: '1' }));
        dispatch(getSoftwareStatusByDeviceKey({ deviceKey: input.deviceKey }));
      } else {
        dispatch(
          enqueueSnackbarAction({
            message: 'Command update failed',
            options: { key: (Date.now() + Math.random()).toString(), variant: 'error' },
          }),
        );
      }
      const modifiedCommands = getState().commands.commands.filter(
        (command: CommandEntity) =>
          response.data.findIndex((c: CommandEntity) => c.commandId === command.commandId) === -1,
      );
      dispatch({ type: commandsActionNames.SET_COMMANDS, payload: modifiedCommands });
    } catch (error) {
      dispatch(
        enqueueSnackbarAction({
          message: `Command update failed --${error}`,
          options: { key: (Date.now() + Math.random()).toString(), variant: 'error' },
        }),
      );
      dispatch({ type: commandsActionNames.SET_COMMANDS, payload: [] });
    }
  };
